import type { PropsWithChildren } from "react";
import React from "react";

import ClockSM from "@busbud/design-system-components/dist/Icons/Clock/SM";
import DSCLLabel from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import { IconExternalLink, Label, Link } from "@busbud/horizon";
import type { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import type { MappedOperator } from "@busbud/int-schemas/udm";
import { LocationType } from "@busbud/int-schemas/udm";

import { OperatorLogo } from "@app/components/operator-logo";
import { getFormattedDuration } from "@app/helpers/duration";
import { useAppContext } from "@app/helpers/hooks";
import {
  convertDurationInMinutesToIso,
  getMomentDurationInMinutes
} from "@app/helpers/time";
import { getTranslatedVehicleType } from "@app/helpers/vehicle-type.helpers";
import type { TranslatorService } from "@app/services/translator";
import {
  clickedStopDetailsLink,
  clickedStopMapLink
} from "@app/tracking/itinerary-tracking";
import type { NapiOperator } from "@app/types/napi-types";

import { useStyles } from "./generic-stop.styles";
import { CheckpointLayout } from "../checkpoint-layout";
import { CheckpointPath } from "../checkpoint-path";
import { CheckpointTime } from "../checkpoint-time";
import { CityLocation } from "../city-location";

interface Props {
  city_name: string;
  day_difference?: number | null;
  duration?: number | null;
  fallback_name?: string;
  has_indent?: boolean;
  has_line?: boolean;
  hide_location?: boolean;
  hide_time?: boolean;
  icon?: React.ReactNode;
  lat: number | null;
  lon: number | null;
  name: string;
  operator?: NapiOperator | MappedOperator;
  stop_details_link?: string;
  stop_service_name?: string | null;
  stop_service_number?: string | null;
  stop_location_type?: LocationType;
  time: string | null;
  vehicle_type?: VehicleType;
}

export const GenericStop: React.FC<PropsWithChildren<Props>> = ({
  children,
  city_name,
  day_difference = 0,
  duration = 0,
  fallback_name,
  has_indent = true,
  has_line = true,
  hide_location = false,
  hide_time = false,
  icon,
  lat,
  lon,
  name,
  operator,
  stop_details_link,
  stop_location_type,
  stop_service_name,
  stop_service_number,
  time,
  vehicle_type
}) => {
  const { classes } = useStyles();
  const { translator } = useAppContext();

  const has_service_data = !!stop_service_name || !!stop_service_number;

  return (
    <>
      <CheckpointLayout
        has_indent={has_indent}
        slot_start={
          !hide_time &&
          !!time && (
            <CheckpointTime value={time} day_difference={day_difference} />
          )
        }
        slot_middle={<CheckpointPath has_line={has_line} icon={icon} />}
        slot_end={
          <Stack className={classes.info} direction="column" spacing={0.5}>
            <div>
              <CityLocation
                hide_location={hide_location}
                stop_name={name}
                city_name={city_name}
                fallback_name={fallback_name}
              />
              <StopInformation
                stop_location_type={stop_location_type}
                name={name}
                lat={lat}
                lon={lon}
                details_link={stop_details_link}
              />
            </div>

            {!!operator && (
              <OperatorLogo
                operator_name={operator.name}
                logo_url={operator.logo_url}
                display_operated_by={false}
                size="sm"
                className={classes.logo}
                no_wrap
              />
            )}

            {!!has_service_data && (
              <DSCLLabel
                className={classes.description}
                color="text.tertiary.opaque"
                fontWeight="bold"
                size="sm"
              >
                {getServiceInfoString(
                  stop_service_name,
                  stop_service_number,
                  vehicle_type,
                  translator
                )}
              </DSCLLabel>
            )}
            {!!children && <div>{children}</div>}
          </Stack>
        }
      />
      {!!duration && duration > 0 && (
        <CheckpointLayout
          slot_middle={
            <CheckpointPath icon={<ClockSM color="icon.secondary.opaque" />} />
          }
          slot_end={
            <DSCLLabel
              className={classes.duration}
              component="time"
              // @ts-expect-error TS2769
              dateTime={convertDurationInMinutesToIso(duration)}
            >
              {getFormattedDuration(
                getMomentDurationInMinutes(duration),
                translator
              )}
            </DSCLLabel>
          }
        />
      )}
    </>
  );
};

function getServiceInfoString(
  stop_service_name: string | undefined | null,
  stop_service_number: string | undefined | null,
  vehicle_type: VehicleType | undefined,
  translator: TranslatorService
): string {
  const string_builder: string[] = [];

  if (stop_service_name) {
    string_builder.push(stop_service_name);
  }

  if (!!stop_service_number) {
    string_builder.push(" • ");
    if (vehicle_type) {
      const vehicle_name = vehicle_type
        ? getTranslatedVehicleType(vehicle_type, translator)
        : undefined;
      string_builder.push(`${vehicle_name} #`);
    }
    string_builder.push(stop_service_number);
  }

  return string_builder.join("");
}

interface StopInformationProps {
  name: string;
  lat: number | null;
  lon: number | null;
  details_link?: string;
  stop_location_type?: LocationType;
}

const StopInformation: React.FC<StopInformationProps> = ({
  lat,
  lon,
  name,
  details_link,
  stop_location_type
}) => {
  const { tracker, translator, urlBuilder } = useAppContext();

  if (details_link) {
    const onClickStopDetailsLink = () => {
      tracker.track(
        clickedStopDetailsLink({ stop_details_link: details_link! })
      );
    };

    return (
      <Label as="span" fontWeight="bold">
        <Link
          target="_blank"
          disableVisitedStyles
          className="truncate"
          url={details_link}
          onClick={onClickStopDetailsLink}
        >
          {translator.t("!itinerary.details.location-details")}
          <IconExternalLink className="inline" size="sm" />
        </Link>
      </Label>
    );
  }

  if (lat && lon && stop_location_type !== LocationType.CityCenter) {
    const onClickStopMapLink = () => {
      tracker.track(
        clickedStopMapLink({
          stop_details_name: name
        })
      );
    };

    return (
      <Label as="span" fontWeight="bold">
        <Link
          target="_blank"
          className="truncate"
          disableVisitedStyles
          url={urlBuilder.googleMapsUrl(lat, lon)}
          onClick={onClickStopMapLink}
        >
          {translator.t("!itinerary.details.map")}
          <IconExternalLink className="inline" size="sm" />
        </Link>
      </Label>
    );
  }

  return null;
};
